import React, { useState } from 'react'
import { LinearProgress, makeStyles, Avatar, Typography, useClientOptions, useIsMobile } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { formatCurrency, formatName, getCrews } from '@paintscout/util/builder'
import classNames from 'classnames'
import type { CalendarEvent } from 'paintscout'
import { CREW_COLORS } from '@paintscout/util'
import ProductionEventPopper from '../ProductionEventPopper'
import { getProjectTypeLabel } from '../util'

const useStyles = makeStyles<Theme, { color: string }>((theme) => ({
  root: {
    padding: theme.spacing(1.5, 2),
    backgroundColor: ({ color }) => `${color}20`,
    borderRadius: theme.borderRadius.md,
    borderLeft: ({ color }) => `4px solid ${color}`,
    cursor: 'pointer',
    boxShadow: theme.shadows[1],
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        backgroundColor: ({ color }) => `${color}40`,
        transition: 'background-color 0.1s ease-in-out'
      }
    }
  },
  content: {
    display: 'flex',
    justifyContent: 'space-between',
    [theme.breakpoints.down('xs')]: {
      paddingBottom: theme.spacing(1.5),
      borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
      marginBottom: theme.spacing(1)
    }
  },
  mainInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5)
  },
  leftContent: {
    display: 'flex',
    gap: theme.spacing(1.5)
  },
  rightContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: theme.spacing(0.5),
    [theme.breakpoints.down('xs')]: {
      display: 'none'
    }
  },
  footer: {
    marginTop: theme.spacing(1.5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5)
  },
  footerLabel: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  smallText: {
    fontSize: theme.typography.pxToRem(12)
  },
  avatar: {
    padding: '0px !important'
  },
  crewAvatarIcon: {
    color: theme.palette.common.white,
    fontSize: theme.typography.pxToRem(10),
    display: 'block',
    lineHeight: 1,
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
    textRendering: 'geometricPrecision'
  },
  progressRoot: {
    height: theme.typography.pxToRem(6),
    borderRadius: '8px',
    backgroundColor: 'rgba(255, 255, 255, 0.4)'
  },
  progressBar: {
    backgroundColor: ({ color }) => color,
    borderRadius: '8px'
  }
}))

interface DayEventProps {
  event: CalendarEvent
}

export function DayEvent(props: DayEventProps) {
  const { event } = props

  const { options } = useClientOptions()
  const mobile = useIsMobile({ xs: true })
  const crews = getCrews({ options, includeTrashed: true })
  const eventCrew = event.crew ? crews.find((crew) => crew._id === event.crew) : null
  const projectTypeLabel = getProjectTypeLabel(event.projectType, options)

  const classes = useStyles({ color: eventCrew?.color || CREW_COLORS[0][0] })

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const subtitle = [projectTypeLabel, eventCrew?.siteLead ? formatName(eventCrew.siteLead) : null]
    .filter(Boolean)
    .join(' · ')

  const mobileSubTitle = eventCrew?.siteLead ? formatName(eventCrew.siteLead) : ''

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget)
  }

  return (
    <>
      <div className={classes.root} onClick={handleClick}>
        <div className={classes.content}>
          <div className={classes.leftContent}>
            <Avatar
              className={classes.avatar}
              disableTooltip
              style={{
                width: '24px',
                height: '24px',
                backgroundColor: eventCrew?.color || CREW_COLORS[0][0],
                color: '#fff',
                transform: 'none'
              }}
            >
              <i className={`fa-light fa-users ${classes.crewAvatarIcon}`} />
            </Avatar>
            <div className={classes.mainInfo}>
              <Typography weight={500}>{event?.deal?.name}</Typography>
              {mobile && !!projectTypeLabel && (
                <Typography variant="caption" color="textSecondary" className={classes.smallText}>
                  {projectTypeLabel}
                </Typography>
              )}
              <Typography variant="caption" color="textSecondary">
                {event.address}
              </Typography>
              {!mobile && !!subtitle && (
                <Typography variant="caption" color="textSecondary" className={classes.smallText}>
                  {subtitle}
                </Typography>
              )}
            </div>
          </div>
          <div className={classes.rightContent}>
            <Typography>{formatCurrency({ options, value: event.deal?.value })}</Typography>
            <Typography variant="caption" color="textSecondary" className={classes.smallText}>
              {event.deal?.hours ? `${event.deal?.hours}h estimated` : ''}
            </Typography>
          </div>
        </div>
        {mobile && !!mobileSubTitle && (
          <Typography variant="caption" color="textSecondary" className={classes.smallText}>
            {mobileSubTitle}
          </Typography>
        )}
        <div className={classes.footer}>
          <Typography
            variant="caption"
            color="textSecondary"
            className={classNames(classes.smallText, classes.footerLabel)}
          >
            <span>Progress</span>
            <span>{event.progress ? `${Math.round(event.progress)}%` : '0%'}</span>
          </Typography>
          <LinearProgress
            classes={{
              root: classes.progressRoot,
              barColorPrimary: classes.progressBar
            }}
            value={event.progress || 0}
            variant="determinate"
          />
        </div>
      </div>
      <ProductionEventPopper event={event} onClose={() => setAnchorEl(null)} anchorEl={anchorEl} />
    </>
  )
}
